import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type UploadStatus = "uploading" | "success" | "error";

export interface UploadProgressState {
  id: string;
  fileName: string;
  progress: number;
  status: UploadStatus;
  errorMessage?: string;
  timestamp: number;
}

export interface DocumentUploadsState {
  uploads: {
    [id: string]: UploadProgressState;
  };
  isCollapsed: boolean;
}

const initialState: DocumentUploadsState = {
  uploads: {},
  isCollapsed: false,
};

const documentUploadsSlice = createSlice({
  name: "documentUploads",
  initialState,
  reducers: {
    uploadStart: (state, action: PayloadAction<{ id: string; file: File }>) => {
      state.uploads[action.payload.id] = {
        id: action.payload.id,
        fileName: action.payload.file.name,
        progress: 0,
        status: "uploading",
        timestamp: Date.now(),
      };
    },
    uploadProgress: (
      state,
      action: PayloadAction<{ id: string; progress: number }>
    ) => {
      const upload = state.uploads[action.payload.id];
      if (upload) {
        upload.progress = action.payload.progress;
      }
    },
    uploadSuccess: (state, action: PayloadAction<{ id: string }>) => {
      const upload = state.uploads[action.payload.id];
      if (upload) {
        upload.status = "success";
        upload.progress = 100;
      }
    },
    uploadError: (
      state,
      action: PayloadAction<{ id: string; error: string }>
    ) => {
      const upload = state.uploads[action.payload.id];
      if (upload) {
        upload.status = "error";
        upload.errorMessage = action.payload.error;
      }
    },
    clearUploadProgress: (state, action: PayloadAction<{ id: string }>) => {
      const { [action.payload.id]: _, ...rest } = state.uploads;
      state.uploads = rest;
    },
    toggleCollapsed: (state) => {
      state.isCollapsed = !state.isCollapsed;
    },
    setUploadState: (
      state,
      action: PayloadAction<{
        id: string;
        state: UploadProgressState;
      }>
    ) => {
      state.uploads[action.payload.id] = action.payload.state;
    },
    clearAllUploads: (state) => {
      state.uploads = {};
    },
  },
});

export const {
  uploadStart,
  uploadProgress,
  uploadSuccess,
  uploadError,
  clearUploadProgress,
  toggleCollapsed,
  setUploadState,
  clearAllUploads,
} = documentUploadsSlice.actions;

export default documentUploadsSlice.reducer;
